import debug from 'debug'
import { getDefaultStore } from 'jotai'
import { atomWithImmer } from 'jotai-immer'
import type { JsonObject, JsonValue } from 'type-fest'
import { getAppEnv } from 'src/utils/config'
import { genId } from 'src/utils/genId'
import { isAndroidWebview, isIOSWebview } from '../userAgent'

// eslint-disable-next-line no-console
const log = getAppEnv().isProd ? debug('pixai:jsBridge') : console.log

type JsBridgeMessageHandlerAndroid = (
  method: string,
  /** JSON stringified Object */
  args?: string,
  /** An ID to identify the callback function. */
  callbackId?: string,
) => void

type JsBridgeMessageHandlerIOS = (message: {
  method: string
  /** JSON stringified Object */
  data?: string
  /** An ID to identify the callback function. */
  callbackId?: string
}) => void

// Same as Android ATM
type JsBridgeMessageHandlerCallback = JsBridgeMessageHandlerAndroid
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JsBridgeLocalCallback = (result: any) => void

// Declaring global type for custom webview objects
declare global {
  interface Window {
    // iOS Safari (Web -> Client)
    webkit?: {
      messageHandlers?: {
        nativeBridge?: {
          postMessage?: JsBridgeMessageHandlerIOS
        }
      }
    }
    // Android (Web -> Client)
    pixaiAppBridge?: {
      postMessage?: JsBridgeMessageHandlerAndroid
    }
    // Web Callback (Client -> Web)
    pixaiWebBridge?: {
      postMessage?: JsBridgeMessageHandlerCallback
    }
  }
}

const INVOKE_CALLBACK_METHOD = '__bridge__callback'
const ANDROID_BRIDGE_NAME = 'pixaiAppBridge'

const defaultStore = getDefaultStore()
const jsBridgeCallbackStoreAtom = atomWithImmer<
  Record<string, JsBridgeLocalCallback>
>({})

export function createJsBridgeCallback<C extends JsBridgeLocalCallback>(
  callback: C,
): string {
  const callbackId = genId()
  defaultStore.set(jsBridgeCallbackStoreAtom, store => {
    store[callbackId] = async (data: Parameters<C>[0]) => {
      await callback(data)
      defaultStore.set(jsBridgeCallbackStoreAtom, store => {
        delete store[callbackId]
      })
    }
  })
  return callbackId
}

/**
 * (Web -> Client)
 * Calls predefined methods inside jsBridge callee, e.g. App
 *
 * @param method
 * @param args Args has to be an JSON object or undefined. Other types aren't accepted.
 * @param callbackId Optional. Before calling this handler, save the callback function inside jsBridgeCallbackStoreAtom Record, then pass the key here. When a callback is received from JsBridge, this callback function will be called.
 */
export function callJsBridgeNativeHandler(
  method: string,
  args?: JsonObject,
  callbackId?: string,
) {
  log({ method, args, callbackId })

  const parsedArgs = JSON.stringify(args)

  if (isAndroidWebview()) {
    window[ANDROID_BRIDGE_NAME]?.postMessage?.(method, parsedArgs, callbackId)
  } else if (isIOSWebview()) {
    window.webkit?.messageHandlers?.nativeBridge?.postMessage?.({
      method: method,
      data: parsedArgs,
      callbackId: callbackId,
    })
  }
}

/**
 * (Client -> Web)
 * Accepts callback from external jsBridge callers, e.g. App
 *
 * @param method
 * @param args
 * @param callbackId
 * @returns
 */
export function callFromJsBridgeHandler(
  method: string,
  args?: JsonValue,
  callbackId?: string,
) {
  log({ method, args, callbackId })
  // Only accept INVOKE_CALLBACK_METHOD method name
  if (method === INVOKE_CALLBACK_METHOD && callbackId) {
    const callback = defaultStore.get(jsBridgeCallbackStoreAtom)[callbackId]
    log({ callback })
    return callback?.(args)
  }
}
